import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { QUESTIONS_BY_SLUG } from "@/data/questions";
import { ArrowLeft, ArrowRight, HelpCircle } from "lucide-react";

export const Route = createFileRoute("/questions/$slug")({
  head: ({ params }) => {
    const q = QUESTIONS_BY_SLUG[params.slug];
    if (!q) return { meta: [{ title: "Question not found — legaldecoder" }] };
    const url = `https://legaldecoder.io/questions/${params.slug}`;
    return {
      meta: [
        { title: q.metaTitle },
        { name: "description", content: q.metaDescription },
        { property: "og:title", content: q.metaTitle },
        { property: "og:description", content: q.metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: q.title,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: q.intro + " " + q.sections.map((s) => `${s.h}: ${s.p.join(" ")}`).join(" "),
                },
              },
            ],
          }),
        },
      ],
    };
  },
  component: QuestionPage,
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl font-bold">Question not found</h1>
        <Link to="/questions" className="mt-4 inline-block text-primary-glow hover:underline">
          Back to all questions
        </Link>
      </div>
    </div>
  ),
});

function QuestionPage() {
  const { slug } = Route.useParams();
  const q = QUESTIONS_BY_SLUG[slug];
  if (!q) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold">Question not found</h1>
          <Link to="/questions" className="mt-4 inline-block text-primary-glow hover:underline">
            Back to all questions
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="relative overflow-hidden border-b border-border">
          <div className="absolute inset-0 bg-gradient-glow" aria-hidden />
          <div className="relative mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
            <Link
              to="/questions"
              className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> All questions
            </Link>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <HelpCircle className="h-3.5 w-3.5" /> Plain-English answer
            </span>
            <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">{q.title}</h1>
            <p className="mt-3 text-lg text-muted-foreground">{q.intro}</p>
          </div>
        </section>

        <article className="mx-auto max-w-3xl space-y-8 px-4 py-12 sm:px-6 lg:px-8">
          {q.sections.map((s) => (
            <section key={s.h}>
              <h2 className="mb-2 text-xl font-bold">{s.h}</h2>
              {s.p.map((para, i) => (
                <p key={i} className="text-base leading-relaxed text-foreground/90">
                  {para}
                </p>
              ))}
            </section>
          ))}

          <div className="rounded-2xl border border-primary-glow/30 bg-gradient-to-br from-primary/10 to-primary-glow/5 p-6">
            <h2 className="text-xl font-bold">Run this against your contract</h2>
            <p className="mt-2 text-muted-foreground">
              Upload the actual document and legaldecoder will flag every issue we just covered — in
              seconds, in plain English.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {q.relatedTools.map((t) => (
                <Link
                  key={t.slug}
                  to="/tools/$slug"
                  params={{ slug: t.slug }}
                  className="inline-flex items-center gap-1 rounded-lg bg-primary-glow px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary"
                >
                  Open {t.name} <ArrowRight className="h-4 w-4" />
                </Link>
              ))}
            </div>
          </div>

          {q.relatedClauses.length > 0 && (
            <section>
              <h2 className="mb-3 text-xl font-bold">Related clause explainers</h2>
              <ul className="grid gap-3 sm:grid-cols-2">
                {q.relatedClauses.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to="/clauses/$slug"
                      params={{ slug: c.slug }}
                      className="group flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-card hover:border-primary-glow/40"
                    >
                      <span className="text-sm font-semibold group-hover:text-primary-glow">
                        {c.name}
                      </span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary-glow" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {q.relatedQuestions.length > 0 && (
            <section>
              <h2 className="mb-3 text-xl font-bold">Related questions</h2>
              <ul className="grid gap-3 sm:grid-cols-2">
                {q.relatedQuestions.map((r) => (
                  <li key={r.slug}>
                    <Link
                      to="/questions/$slug"
                      params={{ slug: r.slug }}
                      className="group flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-card hover:border-primary-glow/40"
                    >
                      <span className="text-sm font-semibold group-hover:text-primary-glow">
                        {r.name}
                      </span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary-glow" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <p className="border-t border-border pt-6 text-center text-xs uppercase tracking-wider text-muted-foreground">
            Not legal advice. For informational purposes only.
          </p>
        </article>
        <Footer />
      </main>
    </div>
  );
}
